import { useContext, useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { clearGlobalDataAfterBuy, setCupon, setResumenDataGTM} from '../globalslice';
import { fetchEvo, fetchSPEI, fetchPaypal, fetchNbps } from '../services/PaymentManager';
import { addOrderPayPalPlus } from '../services/Payments/PayPalPlus/addOrderPayPalPlus';
import { AuthContext } from '../Auth';
import { transformProducts } from './productHelper';
import { clearProfileDataAfterBuy} from '../slices/profileSlice';
import Loader from '../components/Loader';
import { ErrorTransaction, ShowError } from '../alerts/Error';
import loadEvoPaymentsScript from '../components/Resumen/loadEvoPaymentScript';
import { addOrderSaF } from '../services/Payments/SAF/addOrderSaF';
import Swal from 'sweetalert2';

const SendCheckoutEventNew = () => {
    const initialized = useRef(false);
    const descuento = useSelector(state => state.global.descuento);
    const location = useLocation();
    const ResumenData = useSelector(state => state.global.ResumenData);
    const profile = useSelector(state => state.profile);
    const [loader, setLoader] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const showPaymentPage = () => {
        window.Checkout.showPaymentPage();
    };

    // reinicia valores después de crear orden
    const resetAfterCreateOrder = () => {
        dispatch(clearProfileDataAfterBuy());
        dispatch(clearGlobalDataAfterBuy());
        dispatch(setCupon(''));
    }
    const { userAuth } = useContext(AuthContext);
    
    // redirecciona a viewinvoice
    const redirectToErrorView = (invoiceId) => {
        const urlClientes = process.env.REACT_APP_CLIENTES_BASE_URL;
        window.location.replace(urlClientes + "viewinvoice.php?id=" + invoiceId + "&errortransfer=true");
    };

    const obtenerTLD = (dominio) => {
        const partesDominio = dominio.split(".");
        let tld = '';
        for (let i = 1; i < partesDominio.length; i++) {
            tld = tld + "." + partesDominio[i];
        }
        return tld;
    }

    const handlePasoCheckout = async (paymentMethod) => {
        setLoader(true);
        const products = transformProducts(ResumenData);
        try {
            switch (paymentMethod) {
                case 'Evo':
                    // Load the Evo Payments script before configuring
                    await loadEvoPaymentsScript();
    
                    let response = await fetchEvo(products, userAuth.token, null, profile.facturacionAutomatica, profile.discount.nbps, descuento);
                    const sessionId = response.data.sessionid;
                    setLoader(false);
                    window.Checkout.configure({
                        session: {
                            id: sessionId
                        }
                    });
                    showPaymentPage();
                    
                    resetAfterCreateOrder();
                    break;
                case 'PayPal':
                    let pp = await fetchPaypal(products, userAuth.token, profile.discount.nbps, null, profile.facturacionAutomatica, descuento);
                    setLoader(false);
                    if(pp && pp.result === 'error') {
                        ShowError(undefined, "Error", pp.message)
                    } else {
                        resetAfterCreateOrder();
                        if(pp.data.success === true) {
                            window.open(pp.data.approvalLink, '_self', 'noopener,noreferrer');
                        } else {
                            ErrorTransaction(redirectToErrorView(pp.data.invoiceId))
                        }
                    }
                    break;
                case 'PayPalPlus':
                    const responsePPP = await addOrderPayPalPlus({ products, token: userAuth.token, facturacionAutomatica: profile.facturacionAutomatica, nbps: profile.discount.nbps, promocode: descuento });
                    setLoader(false);
                    if (responsePPP) {
                        resetAfterCreateOrder();
                        navigate('/checkout/paypalplus/' + responsePPP.data.invoiceid); 
                    }
                    break;
                case 'Spei':
                    const responseFetch = await fetchSPEI({ products, token: userAuth.token, facturacionAutomatica: profile.facturacionAutomatica, nbps: profile.discount.nbps, promocode: descuento });
                    setLoader(false);
                    if (responseFetch) {
                        resetAfterCreateOrder();
                        navigate('/checkout/spei/' + responseFetch.invoiceid);
                    }
                    break;
                case 'Oxxo':
                    setLoader(false);
                    navigate('/checkout/oxxo/', { state: {products} });
                    break;
                case 'Nbp':
                    let nbp = await fetchNbps(products, userAuth.token, descuento);
                    setLoader(false);
                    if(nbp.success) {
                        navigate('confirm/'+nbp.data.invoiceId+'?compra=exitosa', { state: `$${nbp.data.cost}` });
                        resetAfterCreateOrder();
                    } else {
                        Swal.fire({
                            title: "Error!",
                            text: `${nbp.message}`,
                            icon: "error",
                            confirmButtonText: "OK",
                        });
                    }
                    break;
                case 'Saf':
                    const responseSAF = await addOrderSaF({ products, token: userAuth.token, facturacionAutomatica: profile.facturacionAutomatica, nbps: profile.discount.nbps, promocode: descuento });
                    setLoader(false);
            
                    if (responseSAF) {
                        resetAfterCreateOrder();
                        navigate('/checkout/saf/' + responseSAF.data.invoiceid); 
                    }
                    break;
                default:
                    throw new Error('Unsupported payment method');
                    break;
            }
        } catch (error) {
            Swal.fire({
                title: 'Error!',
                text: error.message,
                icon: 'error',
                confirmButtonText: 'OK'
            });
            console.error('Failed to process payment:', error);
        }
    }

    // Envio de evento GTM
    const { currencyState, productsState, cuponState, paymentMethodState, ivaLocalState, totalLocalState } = location.state || {};

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;

            const listItem = productsState.map(item => ({
                item_id: item.product.id ? item.product.id :
                    item.product.domain_section[0].domain_type === "register" ? "1" :
                    item.product.domain_section[0].domain_type === "transfer" ? "2" :
                    "3",
                item_name: (item.product.id) ? item.product.smallname : obtenerTLD(item.product.domain_section[0].dominio_principal),
                affiliation: "NEUBOX",
                discount: item.price.cross_price ? item.price.cost : "0",
                item_brand: "NEUBOX.COM",
                item_category: item.product.id ? item.product.name.split("-")[0].trim() : "Dominio",
                item_variant: item.periodo,
                price: item.price.second_price ? item.price.second_price : "0",
                quantity: 1,
            }));
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: "checkout",
                ecommerce: {
                    currency: currencyState,
                    value: totalLocalState,
                    coupon: cuponState,
                    payment_type: paymentMethodState === "Evo" ? "banamex" :
                        paymentMethodState === "PayPal" ? "paypal" :
                        paymentMethodState === "PayPalPlus" ? "paypalplus" :
                        paymentMethodState === "Nbp" ? "neuboxpoints" :
                        paymentMethodState === "Saf" ? "creditbalance" :
                        paymentMethodState,
                    tax: ivaLocalState,
                    transaction_id: "",
                    items: listItem
                }
            });

            dispatch(
                setResumenDataGTM({
                    currency : currencyState,
                    ResumenData : productsState,
                    cupon : cuponState,
                    paymentMethod : paymentMethodState,
                    iva : ivaLocalState,
                    total : totalLocalState
                })
            );
            handlePasoCheckout(paymentMethodState);
        }
    });

    return <Loader open={loader} />;
};

export default SendCheckoutEventNew;